// The combat feed: short, loud lines stacked in the corner of a raid, the way a shooter's kill
// feed reads. Missiles inbound, interceptions made and missed, aircraft and drones shot down,
// missiles striking home, warheads going off, with a running interception rate on top. Lines
// slide in, hold a few seconds and fade; a nuke's lines are bigger and hold longer.
import { DEFAULT_CONFIG, type AbilityKind, type CombatEvent } from '@thrash/sim';

const el = document.getElementById('feed')!;
const rateEl = el.querySelector<HTMLElement>('.rate')!;
const list = el.querySelector<HTMLElement>('.lines')!;

/** Most lines on screen at once; the oldest goes when a new one arrives. */
const MAX_LINES = 5;
const HOLD_MS = 3200, NUKE_HOLD_MS = 7000;

let intercepted = 0, leaked = 0;

const unitName = (t: string): string => (DEFAULT_CONFIG.units[t]?.name ?? t).toUpperCase();
const byName = (t: string): string => (DEFAULT_CONFIG.buildings[t]?.name ?? DEFAULT_CONFIG.units[t]?.name ?? t).toUpperCase();
const isMissile = (t: string): boolean => DEFAULT_CONFIG.units[t]?.domain === 'missile' || t === 'nuke' || t === 'emp';
/** The warheads that are nuclear: their lines are the big ones. */
export const NUCLEAR = new Set(['nuke', 'icbm', 'hgv_nuclear']);

/** A line still on screen, by its text: the same thing happening again counts up on it rather than adding another. */
interface Live { row: HTMLElement; count: HTMLElement; n: number; fade: number; gone: number }
const live = new Map<string, Live>();

function line(text: string, tone: 'threat' | 'good' | 'bad' | 'nuke', icon: string): void {
  const hold = tone === 'nuke' ? NUKE_HOLD_MS : HOLD_MS;
  const key = `${tone}|${text}`;
  const was = live.get(key);
  if (was && !was.row.classList.contains('out')) {
    // a swarm: the same line again is one line with a count, moved back to the top and held again
    was.n++;
    was.count.textContent = `×${was.n}`;
    was.row.classList.remove('bump');
    void was.row.offsetWidth;
    was.row.classList.add('bump');
    list.prepend(was.row);
    clearTimeout(was.fade); clearTimeout(was.gone);
    arm(key, was, hold);
    return;
  }
  const row = document.createElement('div');
  row.className = `line ${tone}`;
  row.innerHTML = `<span class="icon">${icon}</span><span class="text"></span><span class="count"></span>`;
  row.querySelector('.text')!.textContent = text;
  const entry: Live = { row, count: row.querySelector('.count')!, n: 1, fade: 0, gone: 0 };
  live.set(key, entry);
  list.prepend(row);
  // the oldest go, but never a nuclear line while it is up
  while (list.childElementCount > MAX_LINES) {
    const victims = [...list.children].reverse() as HTMLElement[];
    const drop = victims.find(r => !r.classList.contains('nuke')) ?? victims[0]!;
    for (const [k, l] of live) if (l.row === drop) { clearTimeout(l.fade); clearTimeout(l.gone); live.delete(k); }
    drop.remove();
  }
  arm(key, entry, hold);
  el.hidden = false;
}

function arm(key: string, l: Live, hold: number): void {
  l.fade = window.setTimeout(() => l.row.classList.add('out'), hold);
  l.gone = window.setTimeout(() => { l.row.remove(); if (live.get(key) === l) live.delete(key); }, hold + 600);
}

function updateRate(): void {
  const total = intercepted + leaked;
  rateEl.textContent = total === 0 ? '' : `INTERCEPTION ${intercepted}/${total} · ${Math.round((intercepted * 100) / total)}%`;
}

/** A line of the raid's own: a wave coming in, say. */
export function feedNote(text: string, tone: 'threat' | 'good' | 'bad' | 'nuke' = 'threat', icon = '▶'): void {
  line(text, tone, icon);
}

/** What the sim reported this tick. */
export function feedEvents(events: readonly CombatEvent[]): void {
  for (const e of events) {
    switch (e.kind) {
      case 'inbound':
        if (NUCLEAR.has(e.what)) line(`NUCLEAR LAUNCH DETECTED · ${unitName(e.what)}${e.by ? ` · ${unitName(e.by)}` : ''}${e.aim ? ` · ${e.aim === 'hq' ? 'COMMAND CENTER' : byName(e.aim)}` : ''}`, 'nuke', '☢');
        else if (e.what === 'emp') line(`EMP MISSILE INBOUND${e.by ? ` · ${unitName(e.by)}` : ''}`, 'threat', '⚡');
        else line(`${unitName(e.what)} INBOUND${e.aim ? ` · ${e.aim === 'hq' ? 'COMMAND CENTER' : byName(e.aim)}` : ''}`, 'threat', '▲');
        break;
      case 'intercepted':
        intercepted++;
        if (NUCLEAR.has(e.what)) line(`NUKE INTERCEPTED · ${byName(e.by ?? '')}`, 'good', '☢');
        else if (e.what === 'emp_missile') line(`EMP MISSILE INTERCEPTED · ${byName(e.by ?? '')}`, 'good', '✓');
        else line(`${byName(e.by ?? '')} INTERCEPTED ${unitName(e.what)}`, 'good', '✓');
        break;
      case 'missed':
        line(`${byName(e.by ?? '')} MISSED ${unitName(e.what)}`, 'bad', '✗');
        break;
      case 'evaded':
        line(`${unitName(e.what)} EVADED ${byName(e.by ?? '')} · FLARES`, 'threat', '✦');
        break;
      case 'downed':
        line(`${byName(e.by ?? '')} DOWNED ${unitName(e.what)}`, 'good', '✈');
        break;
      case 'impact':
        if (isMissile(e.what)) leaked++;
        if (NUCLEAR.has(e.what)) line('NUCLEAR DETONATION', 'nuke', '☢');
        else line(`${unitName(e.what)} STRUCK HOME`, 'bad', '✹');
        break;
      case 'detonated':
        leaked++;
        if (e.what === 'nuke') line('NUCLEAR DETONATION', 'nuke', '☢');
        else if (e.what === 'emp') line('EMP DETONATED · ELECTRONICS DOWN', 'bad', '⚡');
        else line(`${unitName(e.what)} IMPACT`, 'bad', '✹');
        break;
      case 'destroyed':
        line(`${byName(e.what)} DESTROYED`, 'bad', '✹');
        break;
      case 'ability':
        if (e.what === 'artillery') line('ARTILLERY CALLED IN · 3 S', 'good', '◎');
        else if (e.what === 'nuke') line('NUCLEAR STRIKE CALLED IN · 5 S', 'nuke', '☢');
        else if (e.what === 'ac130') line('AC-130 ON STATION · 30 S', 'good', '✈');
        else line(`${(DEFAULT_CONFIG.abilities[e.what as AbilityKind]?.name ?? e.what).toUpperCase()} CALLED IN`, 'good', '✈');
        break;
      case 'shelled':
        if (e.what === 'nuke') { line('NUCLEAR DETONATION · OURS', 'nuke', '☢'); break; }
        line(`SHELLS LANDED${e.by && e.by !== '0' ? ` · ${e.by} DOWN` : ''}`, 'good', '◎');
        break;
    }
  }
  updateRate();
}

/** A new raid: an empty feed and a fresh count. */
export function resetFeed(): void {
  intercepted = 0;
  leaked = 0;
  for (const l of live.values()) { clearTimeout(l.fade); clearTimeout(l.gone); }
  live.clear();
  list.replaceChildren();
  rateEl.textContent = '';
  el.hidden = false;
}

export function hideFeed(): void {
  el.hidden = true;
}
